import {
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import StyledDrawer, {
  StyledDrawerContainer,
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import { GenericDialoagProps } from "Interfaces";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import { useCallback } from "react";

const NumberedList = ({ items }: { items: string[] }) => {
  return (
    <List sx={{ listStyle: "decimal", pl: 5 }}>
      {items.map((item) => (
        <ListItem key={item} sx={{ display: "list-item" }} dense disablePadding>
          <ListItemText
            primary={item}
            primaryTypographyProps={{ fontSize: "12px" }}
          />
        </ListItem>
      ))}
    </List>
  );
};

interface IAddDomainGuideDrawerProps extends GenericDialoagProps {}

function AddDomainGuideDrawer({ open, setOpen }: IAddDomainGuideDrawerProps) {
  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <StyledDrawer open={open} onClose={onClose} maxWidth={600}>
      <StyledDrawerTitle>
        <Typography variant="subtitle1">Subdomain Guide</Typography>
        <Typography variant="body2">
          Step-by-Step Instructions for Linking Your Subdomain to Your Domain
          Provider
        </Typography>
      </StyledDrawerTitle>
      <StyledDrawerContainer>
        <Typography variant="body2">
          You need to add a<strong>CNAME record</strong> at your DNS provider
          for the <strong>subdomain</strong> (points your subdomain to
          Incendium). Copy the exact Key and Value from the subdomain card—do
          not type them manually.
        </Typography>
      </StyledDrawerContainer>
      <Divider />
      <StyledDrawerContainer>
        <Typography variant="body2">
          DNS changes can take a few minutes to several hours to propagate.
          After the record is added, your subdomain will be ready for landing
          pages, campaigns, and smart links.
        </Typography>
      </StyledDrawerContainer>

      <AccordianChartBuilderSidebarBlock
        title="General"
        subTitle="Works for most DNS providers."
        defaultClosed
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Subdomain CNAME</strong>
        </Typography>
        <NumberedList
          items={[
            "Log in and open DNS management for your domain (DNS Settings, DNS Management, or Advanced DNS).",
            'Click "Add record" or "Add DNS record" and choose type "CNAME".',
            'In the Name/Host field: paste the "Domain CNAME Key" from the subdomain card.',
            'In the Value/Points to/Target field: paste the "Domain CNAME Value" from the subdomain card.',
            "Save the record. Your subdomain will activate once DNS propagates.",
          ]}
        />
      </AccordianChartBuilderSidebarBlock>
      <AccordianChartBuilderSidebarBlock
        title="GoDaddy"
        subTitle="DNS for your domain."
        defaultClosed
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Subdomain CNAME</strong>
        </Typography>
        <NumberedList
          items={[
            "Log in at GoDaddy → My Products → Domains. Click your domain.",
            'Under "DNS" click "Manage DNS" (or "DNS Management").',
            'Click "Add" and choose "CNAME".',
            'In "Name"/"Host": paste the Domain CNAME Key.',
            'In "Value"/"Points to": paste the Domain CNAME Value. Save.',
          ]}
        />
      </AccordianChartBuilderSidebarBlock>
      <AccordianChartBuilderSidebarBlock
        title="Namecheap"
        subTitle="Advanced DNS for your domain."
        defaultClosed
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Subdomain CNAME</strong>
        </Typography>
        <NumberedList
          items={[
            "Log in at Namecheap → Domain List → Manage next to your domain.",
            'Open the "Advanced DNS" tab.',
            'Click "Add New Record" → choose "CNAME Record".',
            'In "Host": paste the Domain CNAME Key.',
            'In "Value": paste the Domain CNAME Value. Save.',
          ]}
        />
      </AccordianChartBuilderSidebarBlock>
      <AccordianChartBuilderSidebarBlock
        title="Cloudflare"
        subTitle="DNS records for your zone."
        defaultClosed
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Subdomain CNAME</strong>
        </Typography>
        <NumberedList
          items={[
            "Log in at Cloudflare → select the domain (zone).",
            'Go to "DNS" → "Records".',
            'Click "Add record". Type: CNAME.',
            'In "Name": paste the Domain CNAME Key.',
            'In "Target": paste the Domain CNAME Value. Save. DNS propagation may take a few minutes.',
          ]}
        />
      </AccordianChartBuilderSidebarBlock>
      <StyledDrawerContainer>
        <Typography variant="body2">
          Copy the Domain CNAME Key/Value directly from the subdomain card to
          avoid typos. If your provider’s labels differ (e.g. "Alias" instead of
          "CNAME"), use the CNAME equivalent. For help with your provider’s UI,
          check their DNS or domain documentation.
        </Typography>
      </StyledDrawerContainer>
    </StyledDrawer>
  );
}

export default AddDomainGuideDrawer;
